import { useEffect, useState } from "react"
import { Box, CircularProgress, Drawer, IconButton, Stack, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { useTranslation } from "react-i18next"
import { AuditEventGroup } from "@/models/auditEvent"
import { humanizeModel } from "@/lib/humanize"
import EventGroup from "./components/EventGroup"

type Props = {
    open: boolean
    onClose: () => void
    type: string | null
    id: number | null
}

export default function RecordHistoryDrawer({ open, onClose, type, id }: Props) {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [groups, setGroups] = useState<AuditEventGroup[]>([])
    // Snapshot of the last opened record. The drawer keeps rendering the
    // title and content during the close animation, so falling back to the
    // parent's null props would briefly blank out the header.
    const [shownType, setShownType] = useState<string | null>(null)
    const [shownId, setShownId] = useState<number | null>(null)

    useEffect(() => {
        if (!open || !type || !id) return
        setShownType(type)
        setShownId(id)
        let cancelled = false
        setLoading(true)
        fetch(`/history/record?type=${encodeURIComponent(type)}&id=${id}`, {
            headers: { Accept: "application/json" },
        })
            .then(r => r.json())
            .then(data => {
                if (!cancelled) setGroups(data.groups ?? [])
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [open, type, id])

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                "& .MuiDrawer-paper": {
                    width: { xs: "100%", sm: 560, md: 720, lg: 840 },
                    maxWidth: "100vw",
                },
            }}
        >
            <Box sx={{ p: 3 }}>
                <Stack direction="row" sx={{ mb: 3, alignItems: "center" }}>
                    <Typography variant="h6" sx={{ flex: 1 }}>
                        {t("history.title")} — {shownType ? humanizeModel(t, shownType) : ""} #{shownId ?? ""}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : groups.length === 0 ? (
                    <Box
                        sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            p: 4,
                            textAlign: "center",
                        }}
                    >
                        <Typography color="text.secondary">{t("history.noEvents")}</Typography>
                    </Box>
                ) : (
                    <Stack spacing={1}>
                        {groups.map(group => (
                            <EventGroup key={group.request_id} group={group} />
                        ))}
                    </Stack>
                )}
            </Box>
        </Drawer>
    )
}
